import { Head, Link, router, useForm } from "@inertiajs/react"
import {
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Edit2,
  Trash2,
  ThumbsUp,
  ThumbsDown,
  Eye,
  EyeOff,
  Sparkles,
  Upload,
  X,
  RefreshCw,
} from "lucide-react"
import { useState, useCallback, useRef, useEffect } from "react"
import { AdminLayout } from "../components/AdminLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { RichTextEditor } from "@/components/ui/rich-text-editor"
import { Switch } from "@/components/ui/switch"
import { Separator } from "@/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { AiChatPanel } from "@/components/ai/AiChatPanel"
import { PodcastPanel } from "@/components/podcast/PodcastPanel"
import { US_STATES } from "@/lib/us-states"
import { COUNTRIES } from "@/lib/countries"

const statusConfig = {
  draft: { label: "Draft", variant: "secondary", icon: FileText },
  pending_review: { label: "Pending Review", variant: "warning", icon: Clock },
  published: { label: "Published", variant: "success", icon: CheckCircle },
  rejected: { label: "Rejected", variant: "destructive", icon: AlertCircle },
}

function StatusBadge({ status }) {
  const config = statusConfig[status] || statusConfig.draft
  return (
    <Badge
      variant={config.variant}
      className={
        config.variant === "warning"
          ? "bg-amber-500 text-white hover:bg-amber-500"
          : config.variant === "success"
          ? "bg-green-500 text-white hover:bg-green-500"
          : ""
      }
    >
      {config.label}
    </Badge>
  )
}

function formatDate(dateString) {
  if (!dateString) return "-"
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

export default function ArticleShow({
  currentUser,
  article,
  categories,
  coaches,
  voices,
  errors,
  writingGuidelines,
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [rejectionReason, setRejectionReason] = useState("")
  const [aiPanelOpen, setAiPanelOpen] = useState(false)
  const [selectedParagraph, setSelectedParagraph] = useState(null)
  const [editor, setEditor] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const fileInputRef = useRef(null)

  const { data, setData, patch, processing, transform } = useForm({
    title: article.title || "",
    slug: article.slug || "",
    content: article.content || "",
    excerpt: article.excerpt || "",
    category_id: article.categoryId?.toString() || "",
    author_id: article.author?.id?.toString() || "",
    publish_date: article.publishDate || "",
    featured_image_url: (!article.featuredImageAttached && article.featuredImageUrl) || "",
    featured_image: null,
    _purge_featured_image: false,
    meta_description: article.metaDescription || "",
    meta_keywords: article.metaKeywords || "",
    company: article.company || "",
    job_title: article.jobTitle || "",
    city: article.city || "",
    state: article.state || "",
    country: article.country || "US",
    interviewee_name: article.intervieweeName || "",
    interviewee_email: article.intervieweeEmail || "",
    interviewee_website: article.intervieweeWebsite || "",
    interviewee_twitter: article.intervieweeTwitter || "",
    interviewee_linkedin: article.intervieweeLinkedin || "",
    interviewee_facebook: article.intervieweeFacebook || "",
    interviewee_instagram: article.intervieweeInstagram || "",
    interviewee_youtube: article.intervieweeYoutube || "",
    featured: article.featured || false,
    podcast_enabled: article.podcastEnabled || false,
    podcast_style: article.podcastStyle || "",
    podcast_host_voice_id: article.podcastHostVoiceId?.toString() || "",
    podcast_interviewee_voice_id:
      article.podcastIntervieweeVoiceId?.toString() || "",
    podcast_additional_notes: article.podcastAdditionalNotes || "",
    podcast_host_name: article.podcastHostName || "",
    podcast_interviewee_name: article.podcastIntervieweeName || "",
    podcast_host_name_phonetic: article.podcastHostNamePhonetic || article.authorNamePhonetic || "",
    podcast_interviewee_name_phonetic: article.podcastIntervieweeNamePhonetic || "",
  })

  // Never send featured_image unless it's a real upload — a null value
  // reaches the controller as "" (Inertia coerces null to an empty string
  // in FormData), which the featured_image:"" purge bug then detached on
  // every save. Omitting the key entirely is the frontend defense; the
  // controller strips a blank value regardless as a backstop.
  useEffect(() => {
    transform((currentData) => {
      if (currentData.featured_image instanceof File) return currentData
      const { featured_image, ...rest } = currentData
      return rest
    })
  }, [transform])

  const handleSave = () => {
    patch(`/app/admin/articles/${article.id}`, {
      preserveScroll: true,
      forceFormData: true,
      onSuccess: () => {
        setIsEditing(false)
        setImagePreview(null)
        setData("_purge_featured_image", false)
      },
    })
  }

  const handleCancel = () => {
    setData({
      title: article.title || "",
      slug: article.slug || "",
      content: article.content || "",
      excerpt: article.excerpt || "",
      category_id: article.categoryId?.toString() || "",
      author_id: article.author?.id?.toString() || "",
      publish_date: article.publishDate || "",
      featured_image_url: (!article.featuredImageAttached && article.featuredImageUrl) || "",
      featured_image: null,
      _purge_featured_image: false,
      meta_description: article.metaDescription || "",
      meta_keywords: article.metaKeywords || "",
      company: article.company || "",
      job_title: article.jobTitle || "",
      city: article.city || "",
      state: article.state || "",
      country: article.country || "US",
      interviewee_name: article.intervieweeName || "",
      interviewee_email: article.intervieweeEmail || "",
      interviewee_website: article.intervieweeWebsite || "",
      interviewee_twitter: article.intervieweeTwitter || "",
      interviewee_linkedin: article.intervieweeLinkedin || "",
      interviewee_facebook: article.intervieweeFacebook || "",
      interviewee_instagram: article.intervieweeInstagram || "",
      interviewee_youtube: article.intervieweeYoutube || "",
      featured: article.featured || false,
      podcast_enabled: article.podcastEnabled || false,
      podcast_style: article.podcastStyle || "",
      podcast_host_voice_id: article.podcastHostVoiceId?.toString() || "",
      podcast_interviewee_voice_id:
        article.podcastIntervieweeVoiceId?.toString() || "",
      podcast_additional_notes: article.podcastAdditionalNotes || "",
      podcast_host_name: article.podcastHostName || "",
      podcast_interviewee_name: article.podcastIntervieweeName || "",
      podcast_host_name_phonetic: article.podcastHostNamePhonetic || article.authorNamePhonetic || "",
      podcast_interviewee_name_phonetic: article.podcastIntervieweeNamePhonetic || "",
    })
    setImagePreview(null)
    setIsEditing(false)
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setData((prev) => ({ ...prev, featured_image: file, featured_image_url: "", _purge_featured_image: false }))
      const reader = new FileReader()
      reader.onload = (e) => setImagePreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const handleUrlChange = (url) => {
    setData((prev) => ({ ...prev, featured_image_url: url, featured_image: null, _purge_featured_image: false }))
    setImagePreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const clearFeaturedImage = () => {
    setData((prev) => ({ ...prev, featured_image: null, featured_image_url: "", _purge_featured_image: true }))
    setImagePreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const currentImageUrl = imagePreview || data.featured_image_url || (article.featuredImageAttached && article.featuredImageUrl) || null

  const handleDelete = () => {
    router.delete(`/app/admin/articles/${article.id}`)
  }

  const handleRegenerate = () => {
    router.post(`/app/admin/articles/${article.id}/regenerate`)
  }

  const handleApprove = () => {
    router.post(`/app/admin/articles/${article.id}/approve`)
  }

  const handleReject = () => {
    router.post(`/app/admin/articles/${article.id}/reject`, {
      rejection_reason: rejectionReason,
    })
  }

  const handlePublish = () => {
    router.post(`/app/admin/articles/${article.id}/publish`)
  }

  const handleUnpublish = () => {
    router.post(`/app/admin/articles/${article.id}/unpublish`)
  }

  const handleRestoreOriginalAuthor = () => {
    router.post(`/app/admin/articles/${article.id}/restore_original_author`)
  }

  const handleParagraphSelect = useCallback((paragraph) => {
    setSelectedParagraph(paragraph)
    if (!aiPanelOpen) setAiPanelOpen(true)
  }, [aiPanelOpen])

  const toggleAiPanel = () => {
    setAiPanelOpen(!aiPanelOpen)
    if (aiPanelOpen) {
      setSelectedParagraph(null)
      editor?.commands.clearParagraphSelection?.()
    }
  }

  const isPendingReview = article.status === "pending_review"
  const isPublished = article.status === "published" && article.publishedAt

  return (
    <AdminLayout currentUser={currentUser}>
      <Head title={`${article.title} - Kingdom Factor Admin`} />

      <div className={aiPanelOpen ? "grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-0 -m-4 lg:-m-6 min-h-[calc(100vh-4rem)]" : ""}>
        {/* Main content area */}
        <div className={aiPanelOpen ? "p-4 lg:p-6 overflow-y-auto" : ""}>
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4">
              <Link
                href="/app/admin/articles"
                className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Articles
              </Link>

              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-zinc-900">
                      {article.title}
                    </h1>
                    <StatusBadge status={article.status} />
                  </div>
                  <p className="text-sm text-zinc-500">
                    by {article.author?.name || article.author?.email}
                  </p>
                  {article.rejectionReason && (
                    <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-md">
                      <strong>Rejection reason:</strong> {article.rejectionReason}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {isEditing && (
                    <Button
                      variant={aiPanelOpen ? "outline" : "ghost"}
                      size="sm"
                      onClick={toggleAiPanel}
                      className={aiPanelOpen ? "border-kf-red text-kf-red" : "text-zinc-500"}
                    >
                      <Sparkles className="h-4 w-4" />
                      <span className="hidden sm:inline">AI</span>
                    </Button>
                  )}
                  {!isEditing ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => setIsEditing(true)}
                        className="border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                      >
                        <Edit2 className="h-4 w-4" />
                        Edit
                      </Button>
                      {article.canRegenerate && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                            >
                              <RefreshCw className="h-4 w-4" />
                              Regenerate
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-white border-zinc-200">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-zinc-900">
                                Regenerate Article
                              </AlertDialogTitle>
                              <AlertDialogDescription className="text-zinc-500">
                                This replaces the current draft with a fresh
                                AI-generated article from the original meeting
                                transcript. The current version will be
                                discarded. Continue?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="border-zinc-300 text-zinc-700 hover:bg-zinc-100">
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={handleRegenerate}
                                className="bg-zinc-900 hover:bg-zinc-800"
                              >
                                Regenerate
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white border-zinc-200">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-zinc-900">
                              Delete Article
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-zinc-500">
                              Are you sure you want to delete "{article.title}"?
                              This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-zinc-300 text-zinc-700 hover:bg-zinc-100">
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleDelete}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        onClick={handleCancel}
                        className="text-zinc-500"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="gradient"
                        onClick={handleSave}
                        disabled={processing}
                      >
                        {processing ? "Saving..." : "Save Changes"}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Review Actions */}
            {isPendingReview && (
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-amber-900">
                        Review Required
                      </h3>
                      <p className="text-sm text-amber-700">
                        This article is waiting for your review. Approve to publish
                        or reject with feedback.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handleApprove}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <ThumbsUp className="h-4 w-4" />
                        Approve & Publish
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <ThumbsDown className="h-4 w-4" />
                            Reject
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white border-zinc-200">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-zinc-900">
                              Reject Article
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-zinc-500">
                              Please provide a reason for rejection. This will be
                              visible to the author.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <Textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Reason for rejection..."
                            className="bg-white border-zinc-300 text-zinc-900"
                            rows={3}
                          />
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-zinc-300 text-zinc-700 hover:bg-zinc-100">
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleReject}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Reject Article
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Publish/Unpublish Actions */}
            {article.status === "published" && (
              <Card className="bg-white border-zinc-200">
                <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-zinc-900">
                      Publication Status
                    </h3>
                    <p className="text-sm text-zinc-500">
                      {isPublished
                        ? `Published on ${formatDate(article.publishedAt)}`
                        : "Approved but not yet published"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isPublished ? (
                      <Button
                        variant="outline"
                        onClick={handleUnpublish}
                        className="border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                      >
                        <EyeOff className="h-4 w-4" />
                        Unpublish
                      </Button>
                    ) : (
                      <Button
                        onClick={handlePublish}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Eye className="h-4 w-4" />
                        Publish Now
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tabs */}
            <Tabs defaultValue="article">
              <TabsList className="bg-zinc-100 border-zinc-200">
                <TabsTrigger
                  value="article"
                  className="data-[state=active]:bg-white data-[state=active]:text-zinc-900"
                >
                  Article
                </TabsTrigger>
                <TabsTrigger
                  value="podcast"
                  className="data-[state=active]:bg-white data-[state=active]:text-zinc-900"
                >
                  Podcast
                </TabsTrigger>
              </TabsList>

              {/* Article Tab */}
              <TabsContent value="article" className="mt-6">
                <Card className="bg-white border-zinc-200">
                  <CardContent className="p-6 space-y-6">
                    {/* Basic Info */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="title" className="text-zinc-700">
                          Title
                        </Label>
                        <Input
                          id="title"
                          value={data.title}
                          onChange={(e) => setData("title", e.target.value)}
                          disabled={!isEditing}
                          className="bg-white border-zinc-300 text-zinc-900 disabled:opacity-70"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="slug" className="text-zinc-700">
                          URL Slug
                        </Label>
                        <Input
                          id="slug"
                          value={data.slug}
                          onChange={(e) => setData("slug", e.target.value)}
                          disabled={!isEditing}
                          className="bg-white border-zinc-300 text-zinc-900 disabled:opacity-70"
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="category" className="text-zinc-700">
                          Category
                        </Label>
                        <Select
                          value={data.category_id}
                          onValueChange={(value) => setData("category_id", value)}
                          disabled={!isEditing}
                        >
                          <SelectTrigger className="bg-white border-zinc-300 text-zinc-900">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-zinc-200">
                            {categories?.map((cat) => (
                              <SelectItem
                                key={cat.id}
                                value={cat.id.toString()}
                                className="text-zinc-900"
                              >
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="author" className="text-zinc-700">
                          Author
                        </Label>
                        <Select
                          value={data.author_id}
                          onValueChange={(value) => setData("author_id", value)}
                          disabled={!isEditing}
                        >
                          <SelectTrigger className="bg-white border-zinc-300 text-zinc-900">
                            <SelectValue placeholder="Select author" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-zinc-200">
                            {coaches?.map((coach) => (
                              <SelectItem
                                key={coach.id}
                                value={coach.id.toString()}
                                className="text-zinc-900"
                              >
                                {coach.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Only rendered when the article is not with its original
                          author — normally null, so this whole block is absent. */}
                      {article.originalAuthor && (
                        <div className="space-y-2 md:col-span-2">
                          <Label className="text-zinc-700">Original author</Label>
                          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                            <p className="text-sm text-amber-800">
                              Reassigned from{" "}
                              <span className="font-medium">
                                {article.originalAuthor.name}
                              </span>
                            </p>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="ml-auto border-amber-300 text-amber-700 hover:bg-amber-100"
                                >
                                  Restore original author
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-white border-zinc-200">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-zinc-900">
                                    Restore original author
                                  </AlertDialogTitle>
                                  <AlertDialogDescription className="text-zinc-500">
                                    This gives the article back to{" "}
                                    {article.originalAuthor.name}. The
                                    original-author record is cleared once
                                    the article is back with them.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="border-zinc-300 text-zinc-700 hover:bg-zinc-100">
                                    Cancel
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={handleRestoreOriginalAuthor}
                                    className="bg-zinc-900 hover:bg-zinc-800"
                                  >
                                    Restore
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Featured Image */}
                    <div className="space-y-3">
                      <Label className="text-zinc-700">Featured Image</Label>
                      {currentImageUrl && (
                        <div className="relative aspect-[2/1] rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50">
                          <img
                            src={currentImageUrl}
                            alt="Featured image preview"
                            className="w-full h-full object-cover"
                          />
                          {isEditing && (
                            <button
                              type="button"
                              onClick={clearFeaturedImage}
                              className="absolute top-2 right-2 p-1 bg-white/80 hover:bg-white rounded-full shadow"
                            >
                              <X className="h-4 w-4 text-zinc-600" />
                            </button>
                          )}
                        </div>
                      )}
                      {isEditing && (
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              className="border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                            >
                              <Upload className="h-4 w-4" />
                              Upload Image
                            </Button>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/webp,image/png,image/jpeg,image/gif"
                              onChange={handleFileChange}
                              className="hidden"
                            />
                            <p className="text-xs text-zinc-500">
                              Recommended: 2:1 ratio, WebP format.{" "}
                              <a
                                href="https://cloudconvert.com/png-to-webp"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-kf-red underline"
                              >
                                Convert to WebP
                              </a>
                            </p>
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="featured_image_url" className="text-xs text-zinc-500">
                              Or paste image URL
                            </Label>
                            <Input
                              id="featured_image_url"
                              value={data.featured_image_url}
                              onChange={(e) => handleUrlChange(e.target.value)}
                              placeholder="https://..."
                              className="bg-white border-zinc-300 text-zinc-900"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <Switch
                        checked={data.featured}
                        onCheckedChange={(checked) => setData("featured", checked)}
                        disabled={!isEditing}
                      />
                      <Label className="text-zinc-700">Featured Article</Label>
                    </div>

                    {/* Publish date — published articles only. The model normalizes
                        the chosen day to noon UTC so the public Collective pages
                        (which render in the viewer's timezone) show the intended day.
                        Leaving it blank is a no-op; it never clears the publish date. */}
                    {article.status === "published" && (
                      <div className="space-y-2 max-w-xs">
                        <Label htmlFor="publish_date" className="text-zinc-700">
                          Publish Date
                        </Label>
                        <Input
                          id="publish_date"
                          type="date"
                          value={data.publish_date}
                          onChange={(e) => setData("publish_date", e.target.value)}
                          disabled={!isEditing}
                          className="bg-white border-zinc-300 text-zinc-900 disabled:opacity-70"
                        />
                        <p className="text-xs text-zinc-500">
                          The date shown on the public Collective pages. A future
                          date will schedule the article and hide it from the public
                          until then. Leaving this blank keeps the current date.
                        </p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="excerpt" className="text-zinc-700">
                        Excerpt
                      </Label>
                      <Textarea
                        id="excerpt"
                        value={data.excerpt}
                        onChange={(e) => setData("excerpt", e.target.value)}
                        disabled={!isEditing}
                        rows={2}
                        className="bg-white border-zinc-300 text-zinc-900 disabled:opacity-70"
                      />
                    </div>

                    {/* Interviewee Information */}
                    <div>
                      <h3 className="text-sm font-semibold text-zinc-700 mb-1">
                        Interviewee Information
                      </h3>
                      <p className="text-xs text-zinc-500 mb-4">
                        Information about the person featured in this article. Company and Job Title are required for review submission.
                      </p>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="company" className="text-zinc-700">
                            Company <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="company"
                            value={data.company}
                            onChange={(e) => setData("company", e.target.value)}
                            disabled={!isEditing}
                            placeholder="Interviewee's company"
                            className="bg-white border-zinc-300 text-zinc-900 disabled:opacity-70"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="job_title" className="text-zinc-700">
                            Job Title <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="job_title"
                            value={data.job_title}
                            onChange={(e) => setData("job_title", e.target.value)}
                            disabled={!isEditing}
                            placeholder="Interviewee's job title"
                            className="bg-white border-zinc-300 text-zinc-900 disabled:opacity-70"
                          />
                        </div>
                      </div>
                      <div className="space-y-2 mt-4">
                        <Label htmlFor="country" className="text-zinc-700">Country</Label>
                        <select
                          id="country"
                          value={data.country}
                          onChange={(e) => {
                            const newCountry = e.target.value
                            if (newCountry !== data.country) {
                              setData((prev) => ({ ...prev, country: newCountry, state: "" }))
                            }
                          }}
                          disabled={!isEditing}
                          className="flex h-9 w-full rounded-md border border-zinc-300 bg-white px-3 py-1 text-sm text-zinc-900 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-70"
                        >
                          {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2 mt-4">
                        <div className="space-y-2">
                          <Label htmlFor="city" className="text-zinc-700">City</Label>
                          <Input
                            id="city"
                            value={data.city}
                            onChange={(e) => setData("city", e.target.value)}
                            disabled={!isEditing}
                            placeholder="e.g. Dallas"
                            className="bg-white border-zinc-300 text-zinc-900 disabled:opacity-70"
                          />
                        </div>
                        {data.country === "US" ? (
                          <div className="space-y-2">
                            <Label htmlFor="state" className="text-zinc-700">State</Label>
                            <Select
                              value={data.state || ""}
                              onValueChange={(value) => setData("state", value)}
                              disabled={!isEditing}
                            >
                              <SelectTrigger className="bg-white border-zinc-300 text-zinc-900">
                                <SelectValue placeholder="Select state" />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-zinc-200">
                                {US_STATES.map((s) => (
                                  <SelectItem key={s.value} value={s.value} className="text-zinc-900">
                                    {s.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Label htmlFor="state" className="text-zinc-700">State / Province</Label>
                            <Input
                              id="state"
                              value={data.state}
                              onChange={(e) => setData("state", e.target.value)}
                              disabled={!isEditing}
                              placeholder="State or province"
                              className="bg-white border-zinc-300 text-zinc-900 disabled:opacity-70"
                            />
                          </div>
                        )}
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2 mt-4">
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_name" className="text-zinc-700">
                            Interviewee Name
                          </Label>
                          <Input
                            id="interviewee_name"
                            value={data.interviewee_name}
                            onChange={(e) => setData("interviewee_name", e.target.value)}
                            disabled={!isEditing}
                            placeholder="Interviewee's full name"
                            className="bg-white border-zinc-300 text-zinc-900 disabled:opacity-70"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_email" className="text-zinc-700">
                            Interviewee Email
                          </Label>
                          <Input
                            id="interviewee_email"
                            type="email"
                            value={data.interviewee_email}
                            onChange={(e) => setData("interviewee_email", e.target.value)}
                            disabled={!isEditing}
                            placeholder="interviewee@example.com"
                            className="bg-white border-zinc-300 text-zinc-900 disabled:opacity-70"
                          />
                        </div>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2 mt-4">
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_website" className="text-zinc-500">Website</Label>
                          <Input
                            id="interviewee_website"
                            value={data.interviewee_website}
                            onChange={(e) => setData("interviewee_website", e.target.value)}
                            disabled={!isEditing}
                            placeholder="https://example.com"
                            className={`bg-white border-zinc-300 text-zinc-900 disabled:opacity-70 ${errors?.interviewee_website ? "border-red-500" : ""}`}
                          />
                          {errors?.interviewee_website && (
                            <p className="text-xs text-red-500">{errors.interviewee_website[0]}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_linkedin" className="text-zinc-500">LinkedIn</Label>
                          <Input
                            id="interviewee_linkedin"
                            value={data.interviewee_linkedin}
                            onChange={(e) => setData("interviewee_linkedin", e.target.value)}
                            disabled={!isEditing}
                            placeholder="https://linkedin.com/in/..."
                            className={`bg-white border-zinc-300 text-zinc-900 disabled:opacity-70 ${errors?.interviewee_linkedin ? "border-red-500" : ""}`}
                          />
                          {errors?.interviewee_linkedin && (
                            <p className="text-xs text-red-500">{errors.interviewee_linkedin[0]}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_twitter" className="text-zinc-500">Twitter / X</Label>
                          <Input
                            id="interviewee_twitter"
                            value={data.interviewee_twitter}
                            onChange={(e) => setData("interviewee_twitter", e.target.value)}
                            disabled={!isEditing}
                            placeholder="https://x.com/..."
                            className={`bg-white border-zinc-300 text-zinc-900 disabled:opacity-70 ${errors?.interviewee_twitter ? "border-red-500" : ""}`}
                          />
                          {errors?.interviewee_twitter && (
                            <p className="text-xs text-red-500">{errors.interviewee_twitter[0]}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_facebook" className="text-zinc-500">Facebook</Label>
                          <Input
                            id="interviewee_facebook"
                            value={data.interviewee_facebook}
                            onChange={(e) => setData("interviewee_facebook", e.target.value)}
                            disabled={!isEditing}
                            placeholder="https://facebook.com/..."
                            className={`bg-white border-zinc-300 text-zinc-900 disabled:opacity-70 ${errors?.interviewee_facebook ? "border-red-500" : ""}`}
                          />
                          {errors?.interviewee_facebook && (
                            <p className="text-xs text-red-500">{errors.interviewee_facebook[0]}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_instagram" className="text-zinc-500">Instagram</Label>
                          <Input
                            id="interviewee_instagram"
                            value={data.interviewee_instagram}
                            onChange={(e) => setData("interviewee_instagram", e.target.value)}
                            disabled={!isEditing}
                            placeholder="https://instagram.com/..."
                            className={`bg-white border-zinc-300 text-zinc-900 disabled:opacity-70 ${errors?.interviewee_instagram ? "border-red-500" : ""}`}
                          />
                          {errors?.interviewee_instagram && (
                            <p className="text-xs text-red-500">{errors.interviewee_instagram[0]}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_youtube" className="text-zinc-500">YouTube</Label>
                          <Input
                            id="interviewee_youtube"
                            value={data.interviewee_youtube}
                            onChange={(e) => setData("interviewee_youtube", e.target.value)}
                            disabled={!isEditing}
                            placeholder="https://youtube.com/..."
                            className={`bg-white border-zinc-300 text-zinc-900 disabled:opacity-70 ${errors?.interviewee_youtube ? "border-red-500" : ""}`}
                          />
                          {errors?.interviewee_youtube && (
                            <p className="text-xs text-red-500">{errors.interviewee_youtube[0]}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <Separator className="bg-zinc-200" />

                    <div className="space-y-2">
                      <Label htmlFor="content" className="text-zinc-700">
                        Content
                      </Label>
                      <RichTextEditor
                        value={data.content}
                        onChange={(html) => setData("content", html)}
                        placeholder="Write your article content..."
                        editable={isEditing}
                        aiPanelOpen={aiPanelOpen && isEditing}
                        onParagraphSelect={handleParagraphSelect}
                        onEditorReady={setEditor}
                      />
                    </div>

                    <Separator className="bg-zinc-200" />

                    {/* SEO */}
                    <div>
                      <h3 className="text-sm font-semibold text-zinc-700 mb-4">
                        SEO Settings
                      </h3>
                      <div className="grid gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="meta_description" className="text-zinc-500">
                            Meta Description
                          </Label>
                          <Textarea
                            id="meta_description"
                            value={data.meta_description}
                            onChange={(e) =>
                              setData("meta_description", e.target.value)
                            }
                            disabled={!isEditing}
                            rows={2}
                            className="bg-white border-zinc-300 text-zinc-900 disabled:opacity-70"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="meta_keywords" className="text-zinc-500">
                            Keywords
                          </Label>
                          <Input
                            id="meta_keywords"
                            value={data.meta_keywords}
                            onChange={(e) =>
                              setData("meta_keywords", e.target.value)
                            }
                            disabled={!isEditing}
                            className="bg-white border-zinc-300 text-zinc-900 disabled:opacity-70"
                          />
                        </div>
                      </div>
                    </div>

                  </CardContent>
                </Card>
              </TabsContent>

              {/* Podcast Tab */}
              <TabsContent value="podcast" className="mt-6">
                <PodcastPanel
                  article={article}
                  data={data}
                  setData={setData}
                  isEditing={isEditing}
                  voices={voices}
                  isAdmin={true}
                  baseUrl="/app/admin"
                />
              </TabsContent>
            </Tabs>

            {/* Bottom Save Button */}
            {isEditing && (
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={handleCancel}
                  className="text-zinc-500"
                >
                  Cancel
                </Button>
                <Button
                  variant="gradient"
                  onClick={handleSave}
                  disabled={processing}
                >
                  {processing ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            )}

            {/* Article Info */}
            <Card className="bg-white border-zinc-200">
              <CardHeader>
                <CardTitle className="text-sm text-zinc-700">
                  Article Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <dt className="text-zinc-400">Author</dt>
                    <dd className="text-zinc-900">
                      {article.author?.name || article.author?.email}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-400">Created</dt>
                    <dd className="text-zinc-900">
                      {formatDate(article.createdAt)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-400">Updated</dt>
                    <dd className="text-zinc-900">
                      {formatDate(article.updatedAt)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-400">Published</dt>
                    <dd className="text-zinc-900">
                      {formatDate(article.publishedAt)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-400">Submitted for Review</dt>
                    <dd className="text-zinc-900">
                      {formatDate(article.submittedAt)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-400">Reviewed</dt>
                    <dd className="text-zinc-900">
                      {formatDate(article.reviewedAt)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-400">Reviewed By</dt>
                    <dd className="text-zinc-900">
                      {article.reviewedBy?.name || "-"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-400">Views</dt>
                    <dd className="text-zinc-900">{article.viewCount || 0}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* AI Chat Panel */}
        {aiPanelOpen && isEditing && (
          <div className="hidden lg:block h-[calc(100vh-4rem)] sticky top-16">
            <AiChatPanel
              articleId={article.id}
              baseUrl="/app/admin"
              editor={editor}
              selectedParagraph={selectedParagraph}
              setSelectedParagraph={(p) => {
                setSelectedParagraph(p)
                if (!p) editor?.commands.clearParagraphSelection?.()
              }}
              onClose={toggleAiPanel}
            />
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
